import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, Loader2, Building2, X } from "lucide-react";
import { absoluteUrl } from "../lib/adminApi";
import { Input } from "../components/ui/input";
import { CALL_SALES, WHATSAPP_LINK } from "../lib/constants";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/clients`)
      .then((r) => setClients(r.data.clients || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return clients;
    const q = query.trim().toLowerCase();
    return clients.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [clients, query]);

  return (
    <div data-testid="clients-page" className="bg-white">
      <Seo
        title="Our Clients — Trusted by 10,000+ Businesses Across Nagpur"
        description="Hayat Security is trusted by hospitals, showrooms, offices, warehouses and homes across Nagpur — Sitabuldi, Dharampeth, Civil Lines, MIDC Butibori, Hingna and more. See the brands we secure."
        canonicalPath="/clients"
      />
      <section className="py-8 md:py-10 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Our Clients
          </h1>
          <div className="relative md:max-w-xs w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="clients-search"
              placeholder="Search by name..."
              className="pl-11 h-11 rounded-full bg-slate-50 border-slate-200"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#1C6B8A]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center" data-testid="clients-empty">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-slate-700 font-display font-semibold">
                No clients match &ldquo;{query}&rdquo;
              </div>
            </div>
          ) : (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4"
              data-testid="clients-grid"
            >
              {filtered.map((c, idx) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: (idx % 12) * 0.03 }}
                  data-testid={`client-card-${c.id}`}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-[#1C6B8A]/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col"
                  title={c.name}
                >
                  <div className="relative aspect-[5/3] flex items-center justify-center p-5 bg-white">
                    <img
                      src={absoluteUrl(c.logo_url)}
                      alt={c.name}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="px-3 py-2.5 border-t border-slate-100 bg-slate-50/60">
                    <div className="text-[11px] font-semibold text-slate-700 text-center truncate">
                      {c.name}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-[#1C6B8A] to-[#0f4a63] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Ready to add your name to this list?
          </h2>
          <p className="text-white/80 mt-3 max-w-xl mx-auto">
            Join 10,000+ businesses and homeowners who trust Hayat for their security & surveillance.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <a
              href={CALL_SALES}
              data-testid="clients-cta-call"
              className="h-12 px-6 rounded-full bg-white text-[#1C6B8A] font-semibold inline-flex items-center gap-2 hover:bg-slate-100 transition-colors"
            >
              Call sales
            </a>
            <a
              href={WHATSAPP_LINK("Hi, I'd like to know more about Hayat's CCTV & security services.")}
              target="_blank"
              rel="noreferrer"
              data-testid="clients-cta-whatsapp"
              className="h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#1eb355] text-white font-semibold inline-flex items-center gap-2 transition-colors"
            >
              WhatsApp us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
